import { useState } from 'react';
import { getUserAnalyticsList } from '../api/analyticsApi';
import { useAnalyticsResource } from './useAnalyticsResource';
import { useDebouncedValue } from './useDebouncedValue';
import { DEBOUNCE_MS, DEFAULT_PAGE_SIZE, SEARCH_MAX_LENGTH } from '../constants/userAnalytics';

/**
 * Paged, server-searched list behind one card. Mount it fresh for every open of the popup —
 * that is what resets search, page, page size and the activity period.
 *
 * Page, page size, the debounced search and the applied activity period travel together:
 * whenever any of the other three changes, the page falls back to 1 in the same render, so a
 * search typed on page 3 issues one request (page 1), not a stale page-3 one first.
 */
export function useUsersList({ card, filters, enabled = true, resetKey = '' }) {
  const [searchInput, setSearchInput] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [activity, setActivity] = useState(null);
  const [nav, setNav] = useState({ page: 1, ctx: '' });

  const search = useDebouncedValue(searchInput.trim().slice(0, SEARCH_MAX_LENGTH), DEBOUNCE_MS);
  const ctx = `${search}|${pageSize}|${activity ? `${activity.from}|${activity.to}` : ''}`;
  const page = nav.ctx === ctx ? nav.page : 1;

  const resource = useAnalyticsResource(
    getUserAnalyticsList,
    {
      card,
      ...filters,
      page,
      pageSize,
      search: search || undefined,
      activityFrom: activity?.from,
      activityTo: activity?.to,
    },
    { enabled, resetKey },
  );

  return {
    ...resource,
    page,
    pageSize,
    searchInput,
    searching: searchInput.trim() !== '',
    activity,
    setSearchInput,
    goToPage: (p) => setNav({ page: p, ctx }),
    changePageSize: setPageSize,
    applyActivity: setActivity,
    resetActivity: () => setActivity(null),
  };
}
